import { ClipboardEvent, Ref, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { getUseMessageHistoryStore } from '../../../../stores'
import { webchatClasses } from '../../../../styles/classes'
import { useTranslations } from '../../../../contexts/TranslationContext'

type TextInputProps = {
  inputRef?: Ref<HTMLTextAreaElement>
  composerPlaceholder: string | undefined
  composerDisabled: boolean
  textInput: string
  historyIndex: number
  setTextInput: (value: string) => void
  setHistoryIndex: (index: number) => void
  sendComposerMessage: () => void
  allowFileUpload?: boolean
  onFilePasted?: (file: File) => void
  conversationId?: string
  storageKey?: string
}

export const TextInput = ({
  inputRef,
  composerPlaceholder,
  composerDisabled,
  textInput,
  historyIndex,
  setTextInput,
  setHistoryIndex,
  sendComposerMessage,
  allowFileUpload,
  onFilePasted,
  conversationId,
  storageKey,
}: TextInputProps) => {
  const useMessageHistoryStore = getUseMessageHistoryStore(conversationId || 'default', storageKey)
  const messageHistory = useMessageHistoryStore((state) => state.messageHistory)
  const [draftMessage, setDraftMessage] = useState('')

  const [inputHasBeenTouched, setInputHasBeenTouched] = useState<boolean>(false)
  const { input } = webchatClasses.composer
  const t = useTranslations()

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!allowFileUpload || !onFilePasted || !e.clipboardData) {
      return
    }

    const files = Array.from(e.clipboardData.files)

    if (files.length > 0) {
      e.preventDefault()
      files.forEach((file) => {
        onFilePasted(file)
      })
    }
  }

  return (
    <TextareaAutosize
      {...input}
      aria-label={t['composer.messageInput.ariaLabel']}
      ref={inputRef}
      placeholder={composerPlaceholder ?? t['composer.placeholder']}
      disabled={composerDisabled}
      value={textInput}
      data-has-value={!!textInput}
      onChange={(e) => {
        const newValue = e.target.value
        setTextInput(newValue)
        if (!messageHistory.includes(newValue)) {
          setHistoryIndex(-1)
        }
      }}
      onPaste={handlePaste}
      maxRows={5}
      onKeyDown={(e) => {
        if (composerDisabled) return

        const native = e.nativeEvent as KeyboardEvent
        if (native.isComposing || native.keyCode === 229) {
          //KeyCode 229 is for safari browsers as the isComposing property is not always supported
          return
        }

        if (textInput.length === 0) {
          setInputHasBeenTouched(false)
        }

        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
          setInputHasBeenTouched(true)
        }

        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault()
          setTextInput(`${textInput}\n`)
        } else if (e.key === 'Enter') {
          e.preventDefault()
          sendComposerMessage()
        }

        if (textInput.length > 0 && inputHasBeenTouched) {
          return
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault()

          // Save current draft if we're starting to navigate history
          if (historyIndex === -1 && textInput.trim() !== '') {
            setDraftMessage(textInput)
          }

          const newIndex = Math.min(historyIndex + 1, messageHistory.length - 1)

          setHistoryIndex(newIndex)
          setTextInput(messageHistory[newIndex] ?? '')
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()

          const newIndex = Math.max(historyIndex - 1, -1)

          // Restore draft when going back to the bottom
          if (historyIndex === -1) {
            setTextInput(textInput.trim())
          } else if (newIndex === -1) {
            setTextInput(draftMessage)
          } else {
            setTextInput(messageHistory[newIndex] ?? '')
          }

          setHistoryIndex(newIndex)
        }
      }}
    />
  )
}
